import { cva } from 'class-variance-authority';
import { SIDEBAR_NAV_ITEM_STATE, SIDEBAR_NAV_ITEM_LAYOUT } from '../types/sidebar-theme-enums';
export const sidebarNavItemInteraction = Object.freeze({
  transition:
    'transition-[color,background-color,border-color,box-shadow] duration-fast ease-standard motion-reduce:transition-none',
  hover:
    'pointer-fine:hover:bg-neutral-interaction-hover active:bg-neutral-interaction-pressed',
  selected:
    'text-interaction-selected-indicator pointer-fine:hover:bg-transparent pointer-fine:hover:text-interaction-selected-indicator',
} as const);

function cnBase(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ============================================================================
// SHARED INTERACTION TOKENS
// ============================================================================

const navInteractionTransition = sidebarNavItemInteraction.transition;
const navHover = sidebarNavItemInteraction.hover;
const navSelected = sidebarNavItemInteraction.selected;

// ============================================================================
// CONTAINER RECIPES
// ============================================================================

export const sidebarHeader = cva('flex shrink-0 flex-col gap-1', {
  variants: {
    padding: {
      default: 'px-3 pt-3 pb-0',
      compact: 'p-2',
    },
  },
  defaultVariants: {
    padding: 'default',
  },
});

export const sidebarContent = cva('flex flex-1 flex-col overflow-auto px-2 pb-2', {
  variants: {
    collapsed: {
      true: 'items-center gap-2 px-1.5 pt-2',
      false: 'gap-11 pt-8',
    },
  },
  defaultVariants: {
    collapsed: false,
  },
});

export const sidebarFooter = cva('flex shrink-0 flex-col gap-1 p-2', {
  variants: {
    collapsed: {
      true: 'items-center px-1.5',
      false: '',
    },
  },
  defaultVariants: {
    collapsed: false,
  },
});

// ============================================================================
// SECTION RECIPES
// ============================================================================

export const sidebarSection = cva('flex flex-col gap-1.5');

export const sidebarSectionTitle = cva(
  cnBase(
    'flex h-[28px] cursor-pointer items-center gap-1.5 rounded-control px-2.5',
    'type-micro font-normal text-content-secondary',
    'pointer-coarse:min-h-11',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-ring',
    navInteractionTransition,
    navHover
  ),
  {
    variants: {
      state: {
        default: '',
        active: navSelected,
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

export const sidebarSectionHeading = cva(
  'flex h-auto min-h-0 cursor-default items-center px-2.5 py-0.5 type-micro font-normal text-content-secondary'
);

export const sidebarNestedContainer = cva('flex flex-col gap-1.5');

// ============================================================================
// NAV ITEM RECIPES
// ============================================================================

const sidebarNavItemBase = cnBase(
  'relative mx-1 flex h-[28px] items-center gap-1.5 rounded-control border border-transparent px-2.5',
  'type-nav font-normal no-underline',
  'pointer-coarse:min-h-11',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-ring',
  navInteractionTransition,
  navHover
);

export const sidebarNavItem = cva(sidebarNavItemBase, {
  variants: {
    state: {
      default: 'text-content-primary pointer-fine:hover:text-content-primary',
      active: navSelected,
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

export const sidebarNestedLink = cva(
  cnBase(
    'relative mx-1 flex h-[28px] items-center gap-1.5 rounded-control border border-transparent px-2.5',
    'type-nav font-normal',
    'pointer-coarse:min-h-11',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-ring',
    navInteractionTransition,
    navHover
  ),
  {
    variants: {
      state: {
        default: 'text-content-primary pointer-fine:hover:text-content-primary',
        active: navSelected,
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

export const sidebarFlyoutItem = cva(
  cnBase(
    'relative mx-1 flex h-[28px] w-full items-center gap-1.5 rounded-control border border-transparent px-2.5',
    'text-left type-nav font-normal',
    'pointer-coarse:min-h-11',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-ring',
    navInteractionTransition,
    navHover
  ),
  {
    variants: {
      state: {
        default: 'text-content-primary pointer-fine:hover:text-content-primary',
        active: navSelected,
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

export const sidebarSheetItem = cva(
  cnBase(
    'relative mx-1 flex h-12 w-full items-center gap-1.5 rounded-control border border-transparent px-3',
    'text-left type-nav font-normal',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-ring',
    navInteractionTransition,
    navHover
  ),
  {
    variants: {
      state: {
        default: 'text-content-primary pointer-fine:hover:text-content-primary',
        active: navSelected,
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

// ============================================================================
// ICON RECIPES
// ============================================================================

export const sidebarIcon = cva('h-4 w-4 shrink-0', {
  variants: {
    color: {
      default: '',
      primary: 'text-interaction-selected-indicator',
    },
  },
  defaultVariants: {
    color: 'default',
  },
});

export const sidebarChevron = cva(
  'h-3.5 w-3.5 shrink-0 text-content-tertiary transition-transform duration-fast ease-standard motion-reduce:transition-none',
  {
    variants: {
      state: {
        default: '',
        open: 'rotate-180',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

export const sidebarIconButton = cva(
  cnBase(
    'flex h-10 w-10 cursor-pointer items-center justify-center rounded-control',
    'pointer-coarse:min-h-11 pointer-coarse:min-w-11',
    navInteractionTransition,
    'pointer-fine:hover:bg-neutral-interaction-hover',
    'data-[state=open]:bg-neutral-interaction-selected',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2'
  ),
  {
    variants: {
      state: {
        default:
          'text-content-tertiary pointer-fine:hover:text-content-primary data-[state=open]:text-content-primary',
        active:
          'text-interaction-selected-indicator pointer-fine:hover:text-interaction-selected-indicator data-[state=open]:text-interaction-selected-indicator',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

// ============================================================================
// STATUS INDICATOR
// ============================================================================

export const sidebarStatusIndicator = cva('inline-block h-1.5 w-1.5 rounded-full', {
  variants: {
    status: {
      success: 'bg-status-success',
      warning: 'bg-status-warning',
      error: 'bg-status-danger-solid',
      pending: 'bg-content-disabled',
    },
  },
  defaultVariants: {
    status: 'success',
  },
});

// ============================================================================
// FLAT NESTING WRAPPER
// ============================================================================

export const sidebarItemRail = cva('', {
  variants: {
    depth: {
      default: '',
      nested: '',
    },
    track: {
      true: '',
      false: '',
    },
  },
  defaultVariants: {
    depth: 'default',
    track: false,
  },
});

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function toNavItemStateVariant(
  state: SIDEBAR_NAV_ITEM_STATE
): SIDEBAR_NAV_ITEM_STATE {
  return state === SIDEBAR_NAV_ITEM_STATE.ACTIVE
    ? SIDEBAR_NAV_ITEM_STATE.ACTIVE
    : SIDEBAR_NAV_ITEM_STATE.DEFAULT;
}

import { cn } from '@/lib/utils';

export function getSidebarNavLeafItemClassName({
  state = SIDEBAR_NAV_ITEM_STATE.DEFAULT,
  layout = SIDEBAR_NAV_ITEM_LAYOUT.MENU,
}: {
  state?: SIDEBAR_NAV_ITEM_STATE;
  layout?: SIDEBAR_NAV_ITEM_LAYOUT;
} = {}): string {
  const stateVariant = toNavItemStateVariant(state);

  if (layout === SIDEBAR_NAV_ITEM_LAYOUT.FLYOUT) {
    return cn(sidebarFlyoutItem({ state: stateVariant as 'default' | 'active' }));
  }
  if (layout === SIDEBAR_NAV_ITEM_LAYOUT.SHEET) {
    return cn(sidebarSheetItem({ state: stateVariant as 'default' | 'active' }));
  }
  return cn(sidebarNavItem({ state: stateVariant as 'default' | 'active' }));
}
